// ============================================================
// Tricorder · Client Detail — per-client operator surface.
//
// Shows: identity, health, update composer, updates timeline,
// threads, repo links, workspace launch. All real data or
// polished empty states.
// ============================================================

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import EmptyState from '../../components/EmptyState'
import { api, isAuthError, type TricorderClient, type TricorderUpdate, type TricorderThread } from '../../api/client'
import { SHELL_TOKENS, text, statusAccent, healthAccent, timeAgo } from '../../shell/styles'
import { recordShellAction } from '../../observability/sentry'

type Tab = 'updates' | 'threads' | 'repos'

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [client, setClient] = useState<TricorderClient | null>(null)
  const [updates, setUpdates] = useState<TricorderUpdate[]>([])
  const [threads, setThreads] = useState<TricorderThread[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState<Tab>('updates')

  // Update composer
  const [updateTitle, setUpdateTitle] = useState('')
  const [updateBody, setUpdateBody] = useState('')
  const [updateType, setUpdateType] = useState('update')
  const [sending, setSending] = useState(false)

  // Thread composer
  const [threadSubject, setThreadSubject] = useState('')
  const [creatingThread, setCreatingThread] = useState(false)

  const loadAll = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const [clientsRes, updatesRes, threadsRes] = await Promise.all([
        api.tricorder.clients(),
        api.tricorder.updates.list(id).catch(() => ({ ok: true as const, updates: [] })),
        api.tricorder.threads.list(id).catch(() => ({ ok: true as const, threads: [] })),
      ])
      const found = clientsRes.clients.find((c) => c.id === id) || null
      setClient(found)
      setUpdates(updatesRes.updates)
      setThreads(threadsRes.threads)
    } catch (err: any) {
      if (!isAuthError(err)) console.warn('[Tricorder] load failed:', err.message)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { void loadAll() }, [loadAll])

  async function handleSendUpdate() {
    if (!id || !updateTitle.trim() || sending) return
    setSending(true)
    try {
      await api.tricorder.updates.create({
        project_id: id,
        update_type: updateType,
        title: updateTitle.trim(),
        body: updateBody.trim() || undefined,
      })
      setUpdateTitle('')
      setUpdateBody('')
      recordShellAction('tricorder.update_sent', { projectId: id })
      void loadAll()
    } catch (err: any) {
      console.warn('[Tricorder] update send failed:', err.message)
    } finally {
      setSending(false)
    }
  }

  async function handleCreateThread() {
    if (!id || !threadSubject.trim() || creatingThread) return
    setCreatingThread(true)
    try {
      await api.tricorder.threads.create({ project_id: id, subject: threadSubject.trim() })
      setThreadSubject('')
      recordShellAction('tricorder.thread_created', { projectId: id })
      void loadAll()
    } catch (err: any) {
      console.warn('[Tricorder] thread create failed:', err.message)
    } finally {
      setCreatingThread(false)
    }
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <EmptyState title="Loading client" body="Hydrating from Tricorder." />
      </div>
    )
  }

  if (!client) {
    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <EmptyState title="Client not found" body={`No project with id ${id}`} />
        <button type="button" onClick={() => navigate('/square/tricorder')} style={backBtnStyle}>
          ← Back to Hub
        </button>
      </div>
    )
  }

  const accent = statusAccent(client.status)
  const score = client.health_score ?? client.health?.health_score ?? null

  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>
      {/* Back */}
      <button type="button" onClick={() => navigate('/square/tricorder')} style={backBtnStyle}>
        ← Tricorder Hub
      </button>

      {/* Identity header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16 }}>
        <div>
          <div style={{ ...text.kicker, fontSize: 10, letterSpacing: '0.28em', color: SHELL_TOKENS.inkFaint, marginBottom: 6 }}>
            TRICORDER · CLIENT
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `${accent}14`, border: `1px solid ${accent}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: accent, fontFamily: 'var(--font-brand)',
            }}>◈</div>
            <div>
              <div style={text.editorial}>{client.client_name || client.name}</div>
              <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted, marginTop: 4, display: 'flex', gap: 10 }}>
                <span style={{ padding: '1px 7px', borderRadius: 4, fontSize: 8, fontWeight: 700, background: `${accent}18`, color: accent, border: `1px solid ${accent}25`, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {client.status}
                </span>
                {client.domain && <span>{client.domain}</span>}
                <span>updated {timeAgo(client.updated_at)}</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionBtn label="Workspace" onClick={() => navigate(`/square/projects/${client.id}`)} />
          {client.github_repo_url && (
            <a href={client.github_repo_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <ActionBtn label="GitHub" onClick={() => {}} />
            </a>
          )}
          {client.gitlab_url && (
            <a href={client.gitlab_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <ActionBtn label="GitLab" onClick={() => {}} />
            </a>
          )}
        </div>
      </div>

      {/* Health strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginBottom: 20 }}>
        <MiniKPI label="Health" value={score !== null ? `${score}%` : '—'} color={score !== null ? healthAccent(score) : SHELL_TOKENS.inkFaint} />
        <MiniKPI label="Updates" value={String(updates.length)} />
        <MiniKPI label="Open threads" value={String(threads.filter((t) => t.status === 'open').length)} color={threads.filter((t) => t.status === 'open').length > 0 ? SHELL_TOKENS.amber : undefined} />
        <MiniKPI label="Last contact" value={client.health?.last_contact_at ? timeAgo(client.health.last_contact_at) : '—'} />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {(['updates', 'threads', 'repos'] as Tab[]).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={{
            padding: '8px 18px', borderRadius: 8,
            border: tab === t ? `1px solid ${SHELL_TOKENS.brandLine}` : `1px solid ${SHELL_TOKENS.border}`,
            background: tab === t ? SHELL_TOKENS.brandSoft : 'transparent',
            color: tab === t ? SHELL_TOKENS.brand : SHELL_TOKENS.inkDim,
            fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
            letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer',
          }}>
            {t}
          </button>
        ))}
      </div>

      {/* Updates tab */}
      {tab === 'updates' && (
        <div>
          {/* Composer */}
          <div style={{
            padding: 16, borderRadius: 12, marginBottom: 14,
            background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
          }}>
            <div style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 10, fontWeight: 700 }}>
              SEND UPDATE
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select value={updateType} onChange={(e) => setUpdateType(e.target.value)} style={selectStyle}>
                <option value="update">Update</option>
                <option value="milestone">Milestone</option>
                <option value="deploy">Deploy</option>
                <option value="feedback">Feedback</option>
                <option value="issue">Issue</option>
              </select>
              <input value={updateTitle} onChange={(e) => setUpdateTitle(e.target.value)} placeholder="Title" style={{ ...inputStyle, flex: 1 }} />
            </div>
            <textarea value={updateBody} onChange={(e) => setUpdateBody(e.target.value)} placeholder="Body (optional)" rows={2} style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: 8 }} />
            <button type="button" onClick={handleSendUpdate} disabled={!updateTitle.trim() || sending} style={{
              padding: '8px 16px', borderRadius: 8,
              border: `1px solid ${SHELL_TOKENS.brandLine}`, background: SHELL_TOKENS.brandSoft,
              color: SHELL_TOKENS.brand, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
              cursor: sending ? 'wait' : 'pointer', opacity: !updateTitle.trim() || sending ? 0.5 : 1,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {sending ? 'Sending...' : 'Send Update'}
            </button>
          </div>

          {/* Timeline */}
          {updates.length === 0 ? (
            <EmptyState title="No updates yet" body="Send the first client update using the composer above." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {updates.map((u) => (
                <div key={u.id} style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
                  borderLeft: `3px solid ${typeAccent(u.update_type)}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, color: typeAccent(u.update_type), letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        {u.update_type}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>
                        {u.title}
                      </span>
                    </div>
                    <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint }}>
                      {timeAgo(u.created_at)}
                    </span>
                  </div>
                  {u.body && (
                    <div style={{ fontSize: 12, color: SHELL_TOKENS.inkMuted, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {u.body}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Threads tab */}
      {tab === 'threads' && (
        <div>
          {/* New thread */}
          <div style={{
            padding: 16, borderRadius: 12, marginBottom: 14,
            background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
            display: 'flex', gap: 8, alignItems: 'flex-end',
          }}>
            <input value={threadSubject} onChange={(e) => setThreadSubject(e.target.value)} placeholder="New thread subject" style={{ ...inputStyle, flex: 1 }} />
            <button type="button" onClick={handleCreateThread} disabled={!threadSubject.trim() || creatingThread} style={{
              padding: '8px 16px', borderRadius: 8,
              border: `1px solid ${SHELL_TOKENS.brandLine}`, background: SHELL_TOKENS.brandSoft,
              color: SHELL_TOKENS.brand, fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)',
              cursor: creatingThread ? 'wait' : 'pointer', opacity: !threadSubject.trim() || creatingThread ? 0.5 : 1,
              letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
            }}>
              Create Thread
            </button>
          </div>

          {threads.length === 0 ? (
            <EmptyState title="No threads yet" body="Create a discussion thread to track conversations with this client." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {threads.map((t) => (
                <div key={t.id} style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
                  display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                }} onClick={() => navigate(`/square/tricorder/${id}/thread/${t.id}`)}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: t.status === 'open' ? SHELL_TOKENS.green : SHELL_TOKENS.inkFaint,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: SHELL_TOKENS.ink }}>{t.subject}</div>
                    <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted }}>
                      {t.message_count} messages · {t.status} · {timeAgo(t.updated_at)}
                    </div>
                  </div>
                  <span style={{ fontSize: 14, color: SHELL_TOKENS.inkFaint }}>→</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Repos tab */}
      {tab === 'repos' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
          <RepoPanel
            provider="GitHub"
            url={client.github_repo_url}
            glyph="◉"
            hint="Code spine · CI/CD · Codespaces"
          />
          <RepoPanel
            provider="GitLab"
            url={client.gitlab_url || client.health?.gitlab_repo}
            glyph="◎"
            hint="Collaboration · delivery · tri-corder"
          />
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------
// Local helpers
// ----------------------------------------------------------------

function typeAccent(t: string): string {
  switch (t) {
    case 'milestone': return SHELL_TOKENS.green
    case 'deploy': return SHELL_TOKENS.cyan
    case 'feedback': return SHELL_TOKENS.amber
    case 'issue': return SHELL_TOKENS.red
    default: return SHELL_TOKENS.brand
  }
}

function MiniKPI({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 10,
      background: SHELL_TOKENS.surface, border: `1px solid ${SHELL_TOKENS.border}`,
    }}>
      <div style={{ fontSize: 8, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkFaint, letterSpacing: '0.10em', textTransform: 'uppercase', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)', color: color || SHELL_TOKENS.ink }}>
        {value}
      </div>
    </div>
  )
}

function RepoPanel({ provider, url, glyph, hint }: { provider: string; url?: string | null; glyph: string; hint: string }) {
  const linked = !!url
  return (
    <div style={{
      padding: 20, borderRadius: 12,
      background: SHELL_TOKENS.surface, border: `1px solid ${linked ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: linked ? SHELL_TOKENS.brandSoft : 'rgba(255,255,255,0.02)',
          border: `1px solid ${linked ? SHELL_TOKENS.brandLine : SHELL_TOKENS.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, color: linked ? SHELL_TOKENS.brand : SHELL_TOKENS.inkFaint,
        }}>
          {glyph}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: SHELL_TOKENS.ink }}>{provider}</div>
          <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted }}>{hint}</div>
        </div>
      </div>
      {linked ? (
        <a href={url!} target="_blank" rel="noopener noreferrer" style={{
          display: 'block', fontSize: 11, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.brand,
          textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {url!.replace(/^https?:\/\//, '')}
        </a>
      ) : (
        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: SHELL_TOKENS.inkMuted }}>
          Not linked · set via project settings or API
        </div>
      )}
    </div>
  )
}

function ActionBtn({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '7px 14px', borderRadius: 8,
      border: `1px solid ${SHELL_TOKENS.border}`, background: 'transparent',
      color: SHELL_TOKENS.inkDim, fontSize: 10, fontWeight: 700,
      fontFamily: 'var(--font-mono)', cursor: 'pointer',
      letterSpacing: '0.04em', textTransform: 'uppercase',
    }}>
      {label}
    </button>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 8,
  border: `1px solid ${SHELL_TOKENS.border}`, background: 'rgba(255,255,255,0.03)',
  color: SHELL_TOKENS.ink, fontSize: 12, fontFamily: 'var(--font-mono)', outline: 'none',
}

const selectStyle: React.CSSProperties = {
  ...inputStyle, cursor: 'pointer', minWidth: 100,
}

const backBtnStyle: React.CSSProperties = {
  background: 'transparent', border: 'none',
  color: SHELL_TOKENS.inkFaint, fontSize: 11, fontFamily: 'var(--font-mono)',
  cursor: 'pointer', marginBottom: 14, padding: 0,
}
